import React, { useState } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import config from 'config'
import PropTypes from 'prop-types'
import querystring from 'querystring'
import { required } from '@cred/shared/validations'
import { minMaxLoanAmount } from 'student-loan-refi/rate-table/validations'
import LendersList from 'components/lenders-list'
import StudentLoanRefiFilterForm from 'components/filter-form'
import Pagination from 'components/styled-pagination'
import { allLenders } from 'student-loan-refi/rate-table/lenders-data'
import withRateScript from 'components/hocs/with-rate-script'
import { STUDENT_LOAN_REFI_FILTER_FORM_INITIAL_VALUES as initialValues } from 'common/constants'
import {
  generateFilterText,
  generateUpdate,
  getFilteredLenders,
  getCreditScore,
  getLoanAmount,
  getPageStats,
  resetFilters,
  getPartnerDefaultFilterValues,
} from '../utils'
import FilterModal from '../components/filter-modal'

const formName = 'refi-filter-form',
  paginationName = 'refi-rate-table',
  LENDER_PER_PAGE = 3,

  getCtaUrl = (utms, omitUtmParams) => {
    const params = {
      utm_source: utms.utm_source,
      utm_medium: utms.utm_medium,
      utm_campaign: utms.utm_campaign,
      utm_content: utms.utm_content,
      credclid: utms.credclid,
      renderId: utms.renderId,
    }

    if (omitUtmParams) {
      return `${config.credibleRoot}/refinance/prequal/signup`
    }

    return `${config.credibleRoot}/refinance/prequal/signup?${querystring.encode(params)}`
  },

  compareLenderRates = (a, b) => parseFloat(a.minRateFixed) - parseFloat(b.minRateFixed),

  /* Get lenders rates from Rates library. */
  getLenders = (props, formFilterValues) => {
    const { isScriptLoaded, isScriptLoadSucceed } = props,
      creditScore = getCreditScore(formFilterValues.creditScoreApprox),
      loanAmount = getLoanAmount(formFilterValues.loanAmount),
      lenders = [],
      filter = {
        rateType: 'apr',
        vertical: 'refi',
        term: null,
      },
      options = {
        decimals: 2,
        append: '',
        prepend: '',
      }

    /* Get the rates after loading the ratesLibrary JS and do it only once. */
    if (isScriptLoaded && isScriptLoadSucceed) {
      getFilteredLenders(allLenders, creditScore, loanAmount).forEach(lender => {
        const minRateFixed = global.ratesLibrary.getRateValue(
            { lenderSlug: lender.rateSlug, name: 'min', variable: false, ...filter }, options,
          ),
          maxRateFixed = global.ratesLibrary.getRateValue(
            { lenderSlug: lender.rateSlug, name: 'max', variable: false, ...filter }, options,
          ),
          minRateVar = global.ratesLibrary.getRateValue(
            { lenderSlug: lender.rateSlug, name: 'min', variable: true, ...filter }, options,
          ),
          maxRateVar = global.ratesLibrary.getRateValue(
            { lenderSlug: lender.rateSlug, name: 'max', variable: true, ...filter }, options,
          )

        /* In case there is any problem searching for rates. */
        if (minRateFixed) {
          lenders.push({ minRateFixed, maxRateFixed, minRateVar, maxRateVar, ...lender })
        }
      })
    }

    return lenders.sort(compareLenderRates)
  },

  StudentLoanRefiRateTable = ({
    formFilterValues,
    utms,
    page,
    variation,
    initialize,
    defaultFilterValues,
    widgetContext,
    omitUtmParams,
    ...props }) => {
    const ctaUrl = getCtaUrl(utms, omitUtmParams),
      [filterValues, setFilterValues] = useState(defaultFilterValues),
      lenders = getLenders(props, filterValues),
      { pageCount, pageFrom, pageTo, showPagination } = getPageStats(variation, lenders, page, LENDER_PER_PAGE)

    return (
      <div>
        <FilterModal formName={formName} filterText={generateFilterText(filterValues)}>
          {({ closeModal }) => (
            <StudentLoanRefiFilterForm
              form={formName}
              withFooter
              initialValues={filterValues}
              closeModal={closeModal}
              update={generateUpdate(formFilterValues, setFilterValues, closeModal)}
              customValidation={[required, minMaxLoanAmount]}
            />
          )}
        </FilterModal>
        <LendersList
          lenders={lenders.slice(pageFrom, pageTo)}
          ctaUrl={ctaUrl}
          name="multiVerticalRefi"
          resetFilters={resetFilters(setFilterValues, initialValues)}
        />
        { showPagination && (
          <Pagination
            pageCount={pageCount}
            name={paginationName}
          />
        )}
      </div>
    )
  },
  mapStateToProps = (state, ownProps) => ({
    formFilterValues: state.form[formName],
    page: state.components.pagination[paginationName],
    defaultFilterValues: {
      ...initialValues,
      ...getPartnerDefaultFilterValues(ownProps.widgetContext, 'student-loan-refi'),
    },
  })

StudentLoanRefiRateTable.propTypes = {
  formFilterValues: PropTypes.object,
  page: PropTypes.number,
  variation: PropTypes.string,
  initialize: PropTypes.func,
  utms: PropTypes.shape({
    utm_source: PropTypes.string,
    utm_campaign: PropTypes.string,
    utm_medium: PropTypes.string,
    utm_content: PropTypes.string,
    credclid: PropTypes.string,
    renderId: PropTypes.string,
  }),
  omitUtmParams: PropTypes.bool,
  widgetContext: PropTypes.object,
  defaultFilterValues: PropTypes.shape({
    loanAmount: PropTypes.string,
    creditScoreApprox: PropTypes.number,
  }),
}

StudentLoanRefiRateTable.defaultProps = {
  page: 0,
  omitUtmParams: false,
}

export { StudentLoanRefiRateTable, getCtaUrl, getLenders }

export default compose(
  withRateScript,
  connect(mapStateToProps),
)(StudentLoanRefiRateTable)
